import { Component, EventEmitter, forwardRef, Inject, Input, OnInit, Output } from '@angular/core';
import { AbstractControl, ControlValueAccessor, FormBuilder, FormGroup, NG_VALUE_ACCESSOR, Validators } from '@angular/forms';
import { combineLatest, Subscription } from 'rxjs';
import { fadeAnimation } from 'src/animations';
import { Priority } from 'src/app/classes';
import { IssueTypes } from 'src/app/lib/shared/globals';
import { IssuePriority } from '../../../classes';
import { IIssueTypeProviderService, ISSUE_TYPE_PROVIDER_SERVICE } from '../../../interfaces';
import { IssueService } from '../../../services/issue.service';

@Component({
  selector: 'app-select-priority-form',
  templateUrl: './select-priority-form.component.html',
  styleUrls: ['./select-priority-form.component.css'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => SelectPriorityFormComponent),
      multi: true
    }
  ],
  animations: [fadeAnimation]
})
export class SelectPriorityFormComponent implements ControlValueAccessor, OnInit {

  @Input() editMode: boolean = true;
  @Input() label: string = "Priorität";
  @Input() placeholder: string;
  @Input() showCurrentValues: boolean = false;
  @Input() appearance: string = 'fill';

  @Output() valueChanged: EventEmitter<number> = new EventEmitter();

  get priority(): Priority {
    return this._priority;
  }
  private _priority: Priority;

  _presetted: number;
  get presetted(): number {
    return this._presetted;
  }

  priorities: IssuePriority[] = [];

  containsChanges: boolean = false;
  formGroup: FormGroup;

  private _subscriptions: Subscription[] = [];

  /**
   * the constructor creates a new instance of the component
   * @param _formBuilder 
   * @param _dataService 
   */
  constructor(
    @Inject(ISSUE_TYPE_PROVIDER_SERVICE) private _issueTypeProviderService: IIssueTypeProviderService,
    private _formBuilder: FormBuilder,
    private _issueService: IssueService
  ) {
    this.formGroup = this._formBuilder.group({
      "priority": null,
      "filter": null
    });
  }
  /**
   * the method is called on component disposition
   */
  ngOnDestroy(): void {
    for (let sub of this._subscriptions) sub.unsubscribe();
    this._subscriptions = [];
  }
  /**
   * the method is called on component initalization
   */
  ngOnInit(): void {
    this._subscriptions.push(...[
      combineLatest([
        this._issueService.getAllIssuePriorities(true),
        this._issueTypeProviderService.getCurrentIssueType()
      ]).subscribe({ next: ([priorities, issueType]) => this.setPriorities(issueType, priorities) })
    ]);
  }

  public onTouched: () => void = () => { };

  registerOnChange(fn: any): void {
    this
      ._subscriptions
      .push(
        this
          .priorityControl
          .valueChanges
          .subscribe(fn)
      );
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  /**
   * the method resets the contactperson
   */
  resetValue() {
    if (this.presetted != this.priorityControl.value) this.containsChanges = true;
    else this.containsChanges = false;
  }

  setDisabledState?(isDisabled: boolean): void {
    isDisabled ? this.formGroup.disable() : this.formGroup.enable();
  }

  /**
   * the method sets the issue priorities in accordance to the issue type
   */
  setPriorities(issueType: number, priorities: IssuePriority[]) {

    switch (issueType) {
      case IssueTypes.Change:
        this.priorities = priorities.filter(x => x.ischangepriority);
        break;

      case IssueTypes.Incident:
        this.priorities = priorities.filter(x => x.isincidentpriority);
        break;

      case IssueTypes.Project:
        this.priorities = priorities.filter(x => x.isprojectpriority);
        break;

      default:
        this.priorities = priorities.filter(x => x.isissuepriority);
        break;
    }
  }
  /**
   * the method sets a contactperson
   * @param identifier
   */
  setPriority(identifier: number) {
    if (identifier) {
      this.priorityControl.setValue(identifier);
      this.valueChanged.emit(identifier);
    } else {
      this.priorityControl.setValue(null);
      this.valueChanged.emit(null);
    }
  }

  writeValue(val: any): void {
    this.priorityControl.setValue(val);
  }

  // CONTROLS
  get priorityControl(): AbstractControl {
    return this.formGroup.controls["priority"];
  }

}
